import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { Chart, registerables } from "chart.js";
import { AnalyticsService } from "../../services/analytics-data.service";
import { takeUntil } from "rxjs";
import { UnsubscribeService } from "src/app/services/unsubscribe.service";
import { AnalyticsInfoDto } from "../../models/analytics.model";

Chart.register(...registerables);

@Component({
  selector: "app-support-analytics",
  templateUrl: "./support-analytics.component.html",
  styleUrls: ["./support-analytics.component.scss"],
  providers: [UnsubscribeService],
})
export class SupportAnalyticsComponent implements OnInit {
  chart: any;
  data: any;

  supportData: AnalyticsInfoDto | null = null;
  supportDataChartStructure: Array<number | string> = [];
  totalRequests = 0;

  constructor(
    private analyticsService: AnalyticsService,
    private unsubscribe$: UnsubscribeService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    this.checkLangChange();
    this.loadInfo();
  }

  loadInfo() {
    this.analyticsService
      .getSupportAnalytics()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.supportData = res;
          this.totalRequests = res.in_process_counter + res.new_counter + res.solved_counter;

          this.supportDataChartStructure.push(res.solved_counter || "");
          this.supportDataChartStructure.push(res.new_counter || "");
          this.supportDataChartStructure.push(res.in_process_counter || "");
          this.renderChar();
        },
      });
  }

  renderChar() {
    this.data = this.initializeChartData();

    const segmentText = {
      id: "segmentText",
      afterDatasetsDraw: (chart: any) => {
        const { ctx } = chart;

        ctx.save();

        ctx.font = "normal 500 1.7rem sans-serif";
        ctx.fillStyle = `rgba(255, 255, 255, 1)`;

        chart.getDatasetMeta(0).data.forEach((datapoint: any, index: number) => {
          const total = this.supportDataChartStructure.reduce((acc: any, val) => acc + val, 0);
          const percentage = Math.round(((this.supportDataChartStructure[index] as any) / total) * 100) + "%";

          const text =
            chart.tooltip._active && chart.tooltip._active.length && chart.tooltip._active[0].index === index
              ? this.supportDataChartStructure[index]
              : percentage;

          const xCoor = datapoint.tooltipPosition().x;
          const yCoor = datapoint.tooltipPosition().y;
          ctx.fillText(text, xCoor, yCoor);
        });
      },
    };

    const doughnutLabel = {
      id: "doughnutLabel",
      beforeDatasetsDraw: (chart: any) => {
        const { ctx } = chart;

        ctx.save();
        const xCoor = chart.getDatasetMeta(0).data[0].x;
        const yCoor = chart.getDatasetMeta(0).data[0].y - 20;

        ctx.font = "normal 700 3rem sans-serif";
        ctx.fillStyle = "rgb(70, 73, 88)";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(`${this.totalRequests}`, xCoor, yCoor);
      },
    };

    const doughnutLabelStat = {
      id: "doughnutLabelStat",
      beforeDatasetsDraw: (chart: any) => {
        const { ctx } = chart;

        ctx.save();
        const xCoor = chart.getDatasetMeta(0).data[0].x;
        const yCoor = chart.getDatasetMeta(0).data[0].y + 10;

        ctx.font = "normal 400 2rem sans-serif";
        ctx.fillStyle = "rgb(146, 145, 165)";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(this.translateService.instant(`ANALYTICS_PAGE.TOTAL_REQUESTS`), xCoor, yCoor);
      },
    };

    this.chart = new Chart("doughnut", {
      type: "doughnut",
      data: this.data,
      plugins: [doughnutLabel, doughnutLabelStat, segmentText],
      options: {
        plugins: {
          title: {
            display: false,
          },
          legend: {
            display: false,
          },
          tooltip: {
            enabled: false,
          },
        },
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 1,
        scales: {
          x: { display: false },
          y: { display: false },
        },
        cutout: 65,
        events: ["mousemove", "mouseout", "click", "touchstart", "touchmove"],
      },
    });
  }

  private initializeChartData() {
    const total = this.supportDataChartStructure.reduce((acc: any, val) => acc + val, 0);
    const minSegmentValue = total * 0.07;
    const adjustedData = this.supportDataChartStructure.map((value: any) => (value < minSegmentValue ? minSegmentValue : value));

    const data = {
      labels: ["Red", "Blue", "Yellow"],
      datasets: [
        {
          label: "My First Dataset",
          data: adjustedData,
          backgroundColor: ["rgb(255, 160, 48)", "rgb(59, 187, 147)", "rgb(76, 113, 239)"],
          radius: 100,
          hoverOffset: 20,
          offset: 4,
          borderRadius: 8,
          spacing: 8,
          responsive: true,

          maintainAspectRatio: false,
          borderWidth: adjustedData.map((value) => (value === minSegmentValue ? 1 : 0)),
        },
      ],
    };
    return data;
  }

  private checkLangChange() {
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: () => {
        if (this.chart) {
          this.chart.destroy();
          this.renderChar();
        }
      },
    });
  }
}
